import React, { CSSProperties, ReactNode, useMemo, useRef, useState } from 'react';
import DataTableGovernance from '../../../components/main/DataTableGovernance/DataTableGovernance';
import GovernanceFilterBar from '../../../components/main/GovernanceFilterBar/GovernanceFilterBar';
import RefreshButton from '../../../components/main/RefreshButton/RefreshButton';
import RequestLibraryCards from '../../../components/main/RequestLibraryCards/RequestLibraryCards';
import { ContentVersionSortInput, DisciplineEnum, useGetRequestsLibrarySummaryQuery, useGetRequestsQuery, RequestStateEnum } from '../../../generated/graphql';
import { createGraphQLOrder, TableSort } from '../../../components/main/DataTableNew/DataTableSorting';

type ApprovedRequestsProps = {
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
    className?: string;
};

function filterRequests(data: any[] | undefined | null, search: string) {
    if (!data || !search) return data;
    const lower = search.toLowerCase();
    return data.filter((r: any) =>
        r.name?.toLowerCase().includes(lower) ||
        r.client?.name?.toLowerCase().includes(lower) ||
        r.discipline?.toLowerCase().includes(lower) ||
        r.status?.toLowerCase().includes(lower) ||
        r.createdBy?.name?.toLowerCase().includes(lower)
    );
}

const ApprovedRequests = (props: ApprovedRequestsProps) => {
    const { style } = props;
    const [discipline, setDiscipline] = useState<DisciplineEnum | null>(null);
    const [sort, setSort] = useState<TableSort | null>({ field: 'updatedTime', direction: 'desc' });
    const [searchText, setSearchText] = useState('');
    const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
    const [selectedLibraryIds, setSelectedLibraryIds] = useState<string[]>([]);
    const loadingMore = useRef(false);

    const requests = useGetRequestsQuery({
        variables: {
            state: RequestStateEnum.Closed,
            discipline: discipline,
            order: createGraphQLOrder<ContentVersionSortInput>(sort, { field: 'updatedTime', direction: 'desc' }),
            where: selectedClientIds.length || selectedLibraryIds.length ? {
                clientId: selectedClientIds.length ? { in: selectedClientIds } : undefined,
                libraryId: selectedLibraryIds.length ? { in: selectedLibraryIds } : undefined,
            } : undefined,
        },
    });
    const summary = useGetRequestsLibrarySummaryQuery({
        variables: {
            state: RequestStateEnum.Closed,
            discipline,
        },
    });

    const filteredData = useMemo(() => filterRequests(requests.data?.Requests?.nodes, searchText), [requests.data, searchText]);

    const fetchMore = async () => {
        const pageInfo = requests.data?.Requests?.pageInfo;
        if (!pageInfo?.hasNextPage || !pageInfo.endCursor || loadingMore.current) return;
        loadingMore.current = true;
        try {
            await requests.fetchMore({
                variables: {
                    after: pageInfo.endCursor,
                    order: createGraphQLOrder<ContentVersionSortInput>(sort, { field: 'updatedTime', direction: 'desc' }),
                    where: selectedClientIds.length || selectedLibraryIds.length ? {
                        clientId: selectedClientIds.length ? { in: selectedClientIds } : undefined,
                        libraryId: selectedLibraryIds.length ? { in: selectedLibraryIds } : undefined,
                    } : undefined,
                },
                updateQuery: (previous, { fetchMoreResult }) => {
                    if (!fetchMoreResult?.Requests) return previous;
                    return {
                        ...previous,
                        Requests: {
                            ...fetchMoreResult.Requests,
                            nodes: [
                                ...(previous.Requests?.nodes ?? []),
                                ...(fetchMoreResult.Requests.nodes ?? []),
                            ],
                        },
                    };
                },
            });
        } finally {
            loadingMore.current = false;
        }
    };

    return (
        <div className="page-container governance-list-page" style={style} data-testid="GovernanceApprovedRequests">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Closed Requests</h1>
                    <p className="page-subtitle">Published, declined, and cancelled requests</p>
                </div>
                <RefreshButton onRefresh={() => { requests.refetch(); summary.refetch(); }} className="btn-secondary" iconSize={16} />
            </div>
            <RequestLibraryCards
                requests={summary.data?.Requests?.nodes}
                selectedClientIds={selectedClientIds}
                selectedLibraryIds={selectedLibraryIds}
                onClientsSelect={setSelectedClientIds}
                onLibrariesSelect={setSelectedLibraryIds}
            />
            <GovernanceFilterBar
                discipline={discipline}
                onDisciplineChange={setDiscipline}
                isAscending={sort?.field === 'updatedTime' && sort.direction === 'asc'}
                onSortChange={(isAscending) => setSort({ field: 'updatedTime', direction: isAscending ? 'asc' : 'desc' })}
            />
            <div className="flex-1 min-h-0 relative">
                <DataTableGovernance
                    onSearchBoxChanged={setSearchText}
                    onRefetch={() => requests.refetch()}
                    fetchMore={fetchMore}
                    hasMore={requests.data?.Requests?.pageInfo.hasNextPage}
                    data={filteredData}
                    isLoading={requests.loading}
                    sort={sort}
                    onSortChange={setSort}
                />
            </div>
        </div>
    );
};

export default ApprovedRequests;
